import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { learningPlanService, lessonService, goalService, roadmapService, subscriptionService, courseService } from '../services/api';
import SideMenu from './SideMenu';
import { useTheme } from '../ThemeContext';
import { Target, Map } from 'react-feather';
import './SideMenu.css';
import './LearningPlan.css';

// RoadmapBlock component to display a roadmap with its lessons
const RoadmapBlock = React.memo(({
  roadmap,
  itemIndex,
  lessons,
  expandedRoadmaps,
  toggleRoadmapExpanded,
  firstUncompletedLessonRoadmapId,
  firstUncompletedLessonId,
  generatingContentRef,
  handleStartLesson,
  theme
}) => {
  const isExpanded = expandedRoadmaps[roadmap.Id];
  const itemLessons = lessons[roadmap.Id] || [];
  const completedLessons = itemLessons.filter(lesson => lesson.IsCompleted).length;
  const totalLessons = itemLessons.length;
  const completionPercentage = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
  const hasLessons = totalLessons > 0;

  // Calculate average score for completed roadmaps
  const calculateAverageScore = (lessons) => {
    const completedLessonsWithScores = lessons.filter(lesson => 
      lesson.IsCompleted && lesson.Grade && lesson.Grade > 0
    );
    
    if (completedLessonsWithScores.length === 0) return null;
    
    const totalScore = completedLessonsWithScores.reduce((sum, lesson) => sum + lesson.Grade, 0);
    return Math.round(totalScore / completedLessonsWithScores.length);
  };

  return (
    <div className={`roadmap-block ${isExpanded ? 'expanded' : ''}`}>
      <div 
        className="roadmap-block-header"
        onClick={() => toggleRoadmapExpanded(roadmap.Id)}
      >
        <div className="roadmap-block-number">{itemIndex + 1}</div>
        <div className="roadmap-block-content">
          <h3>{roadmap.Content || roadmap.Name || `Section ${itemIndex + 1}`}</h3>
          
          {roadmap.IsCompleted ? (
            <div className="roadmap-status completed">
              <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor" />
              </svg>
              <span>Completed</span>
              {(() => {
                const avgScore = calculateAverageScore(itemLessons);
                return avgScore && (
                  <div className="roadmap-average-score">
                    Avg: {avgScore}%
                  </div>
                );
              })()}
            </div>
          ) : hasLessons ? (
            <div className="roadmap-status in-progress">
              <span>{completedLessons} of {totalLessons} lessons ({completionPercentage}%)</span>
            </div>
          ) : (
            <div className="roadmap-status locked">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
                <path d="M8 11V7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <span>Locked</span>
            </div>
          )}
          
          {hasLessons && (
            <button className="expand-button">
              {isExpanded ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 9L12 16L5 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          )}
        </div>
      </div>
      
      {isExpanded && hasLessons && (
        <div className="roadmap-lessons">
          {itemLessons.map((lesson, lessonIndex) => {
            const isFirstUncompleted = !lesson.IsCompleted && lesson.Id === firstUncompletedLessonId;
            
            return (
              <div 
                key={lesson.Id}
                className={`lesson-item ${lesson.IsCompleted ? 'completed' : ''} ${isFirstUncompleted ? 'next-lesson' : ''}`}
              >
                <div className="lesson-number">{lessonIndex + 1}</div>
                <div className={`lesson-checkbox ${lesson.IsCompleted ? 'checked' : ''}`}>
                  {lesson.IsCompleted && (
                    <svg width="12" height="12" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white" />
                    </svg>
                  )}
                </div>
                
                <div className="lesson-content">
                  <div className="lesson-title">{lesson.Title || 'Untitled Lesson'}</div>
                </div>
                
                <div className="lesson-actions">
                  {lesson.IsCompleted && lesson.Grade && lesson.Grade > 0 && (
                    <div className="lesson-score">
                      Score: {lesson.Grade}%
                    </div>
                  )}
                  
                  {lesson.IsCompleted ? (
                    <button
                      className="lesson-action-button view"
                      onClick={(e) => handleStartLesson(lesson, e)}
                    >
                      View Lesson
                    </button>
                  ) : isFirstUncompleted && (
                    <button
                      className="lesson-action-button start"
                      onClick={(e) => handleStartLesson(lesson, e)}
                    >
                      Start Lesson
                    </button>
                  )}
                  
                  {generatingContentRef.current[lesson.Id] && (
                    <div className="generating-badge">
                      <svg className="spinning-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12" stroke="currentColor" strokeWidth="2" fill="none"/>
                      </svg>
                      Generating...
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

// Memoized render learning plan content function
const RenderLearningPlan = React.memo(({
  roadmaps,
  lessons,
  expandedRoadmaps,
  toggleRoadmapExpanded,
  firstUncompletedLessonRoadmapId,
  firstUncompletedLessonId,
  generatingContentRef,
  handleStartLesson,
  handleRefreshData,
  creating,
  theme
}) => {
  // Create a ref for the roadmap items container to measure its width
  const listContainerRef = useRef(null);
  const [listHeight, setListHeight] = useState(window.innerHeight - 200); // Default height
  const [listWidth, setListWidth] = useState(800); // Default width

  // Update list dimensions when window resizes
  useEffect(() => {
    const handleResize = () => {
      if (listContainerRef.current) {
        setListWidth(listContainerRef.current.clientWidth || 800);
        setListHeight(window.innerHeight - 200); // Allow scrolling content
      }
    };

    // Set initial dimensions
    handleResize();

    // Add resize listener
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  console.log('RenderLearningPlan called with:', {
    roadmapsLength: roadmaps?.length,
    roadmaps: roadmaps,
    lessonsKeys: Object.keys(lessons || {})
  });
  
  // Adapted rendering based on Roadmap component's timeline structure
  return (
    <div className="timeline-container">
      <div className="timeline-line"></div>
      
      {roadmaps.map((roadmap, itemIndex) => {
        const isExpanded = expandedRoadmaps[roadmap.Id];
        const itemLessons = lessons[roadmap.Id] || [];
        const completedLessons = itemLessons.filter(lesson => lesson.IsCompleted).length;
        const totalLessons = itemLessons.length;
        const completionPercentage = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
        const hasLessons = totalLessons > 0;
        
        // Calculate average score for completed roadmaps
        const calculateAverageScore = (lessons) => {
          const completedLessonsWithScores = lessons.filter(lesson => 
            lesson.IsCompleted && lesson.Grade && lesson.Grade > 0
          );
          
          if (completedLessonsWithScores.length === 0) return null;
          
          const totalScore = completedLessonsWithScores.reduce((sum, lesson) => sum + lesson.Grade, 0);
          return Math.round(totalScore / completedLessonsWithScores.length);
        };

        return (
          <div key={roadmap.Id} className="milestone-item">
            <div className={`milestone-dot ${roadmap.IsCompleted ? 'milestone-dot-completed' : ''}`}>
              <div className="milestone-number">{itemIndex + 1}</div>
            </div>
            
            <div className={`milestone-content ${theme}`}>
              <div className="lp-roadmap-item-title">
                <h3>{roadmap.Content || roadmap.Name || `Section ${itemIndex + 1}`}</h3>
                
                {roadmap.IsCompleted ? (
                  <span className="milestone-completed">
                    <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor" />
                    </svg>
                    Completed
                    {(() => {
                      const avgScore = calculateAverageScore(itemLessons);
                      return avgScore && (
                        <div className="milestone-average-score">
                          Avg: {avgScore}%
                        </div>
                      );
                    })()}
                    {hasLessons && (
                      <button 
                        onClick={() => toggleRoadmapExpanded(roadmap.Id)}
                        className="expand-button"
                      >
                        {isExpanded ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19 9L12 16L5 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </button>
                    )}
                  </span>
                ) : !hasLessons ? (
                  <span className="milestone-locked">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
                      <path d="M8 11V7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    Locked
                  </span>
                ) : (
                  <span className="milestone-progress">
                    {completedLessons} of {totalLessons} lessons ({completionPercentage}%)
                    {hasLessons && (
                      <button 
                        onClick={() => toggleRoadmapExpanded(roadmap.Id)}
                        className="expand-button"
                      >
                        {isExpanded ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19 9L12 16L5 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </button>
                    )}
                  </span>
                )}
              </div>
              
              {isExpanded && hasLessons && (
                <ul className="topics-list">
                  {itemLessons.map((lesson, lessonIndex) => {
                    const isFirstUncompleted = !lesson.IsCompleted && lesson.Id === firstUncompletedLessonId;
                    
                    return (
                      <li 
                        key={lesson.Id} 
                        className={`topic-item ${lesson.IsCompleted ? 'topic-item-completed' : ''} ${isFirstUncompleted ? 'topic-item-next' : ''}`}
                      >
                        <div className="lesson-number">{lessonIndex + 1}</div>
                        <div className="topic-content">
                          <p className="topic-text">{lesson.Title || 'Untitled Lesson'}</p>
                        </div>
                        
                        {lesson.IsCompleted && (
                          <>
                            {lesson.Grade && lesson.Grade > 0 && (
                              <div className="lesson-score-badge">
                                {lesson.Grade}%
                              </div>
                            )}
                            <button
                              className="lesson-action-button view-lesson"
                              onClick={(e) => handleStartLesson(lesson, e)}
                            >
                              View Lesson
                            </button>
                          </>
                        )}
                        
                        {!lesson.IsCompleted && isFirstUncompleted && (
                          <button 
                            className="lesson-action-button start-lesson"
                            onClick={(e) => handleStartLesson(lesson, e)}
                          >
                            Start Lesson
                          </button>
                        )}
                        
                        {generatingContentRef.current[lesson.Id] && (
                          <span className="generating-badge">
                            <svg className="spinning-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12" stroke="currentColor" strokeWidth="2" fill="none"/>
                            </svg>
                            Generating...
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
});

const LearningPlan = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  
  // State for courses and learning data
  const [coursesData, setCoursesData] = useState(null);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingCourse, setLoadingCourse] = useState(false);
  const [error, setError] = useState(null);

  // State for roadmaps, lessons, and other learning plan data
  const [roadmaps, setRoadmaps] = useState([]);
  const [lessons, setLessons] = useState({});
  const [activeGoal, setActiveGoal] = useState(null);
  const [expandedRoadmaps, setExpandedRoadmaps] = useState({});
  const [firstUncompletedLessonRoadmapId, setFirstUncompletedLessonRoadmapId] = useState(null);
  const [firstUncompletedLessonId, setFirstUncompletedLessonId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showLessonLimitModal, setShowLessonLimitModal] = useState(false);

  // Replace state with ref to avoid re-renders
  const generatingContentRef = useRef({});
  const lastApiCallTimeRef = useRef(0);
  const lastRoadmapsRef = useRef([]);
  const lastLessonsRef = useRef({});
  const firstUncompletedLessonIdRef = useRef(null);
  const isCheckingForLessonsRef = useRef(false);


  // Helper function to calculate if goal is completed based on all roadmaps being completed
  const isGoalCompleted = useMemo(() => {
    if (!roadmaps || roadmaps.length === 0) return false;
    
    // Check if all roadmaps are completed
    const allRoadmapsCompleted = roadmaps.every(roadmap => roadmap.IsCompleted);
    
    // Also check if the activeGoal has IsCompleted property set
    const goalMarkedCompleted = activeGoal?.isCompleted || activeGoal?.IsCompleted;
    
    return allRoadmapsCompleted || goalMarkedCompleted;
  }, [roadmaps, activeGoal]);

  // Helper function to calculate overall completion percentage
  const getOverallCompletionPercentage = useMemo(() => {
    if (!roadmaps || roadmaps.length === 0) return 0;
    
    let totalLessons = 0;
    let completedLessons = 0;
    
    roadmaps.forEach(roadmap => {
      const roadmapLessons = lessons[roadmap.Id] || [];
      totalLessons += roadmapLessons.length;
      completedLessons += roadmapLessons.filter(lesson => lesson.IsCompleted).length;
    });
    
    return totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
  }, [roadmaps, lessons]);

  // Selected course helpers
  const selectedCourseIdValue = useMemo(() => {
    return selectedCourseId ? parseInt(selectedCourseId) : null;
  }, [selectedCourseId]);

  const hasCourseIdInRoadmaps = useMemo(() => {
    if (!Array.isArray(roadmaps)) return false;
    return roadmaps.some(r => typeof (r?.CourseId ?? r?.courseId) !== 'undefined' && (r?.CourseId ?? r?.courseId) !== null);
  }, [roadmaps]);

  const visibleRoadmaps = useMemo(() => {
    const all = Array.isArray(roadmaps) ? roadmaps : [];
    console.log('visibleRoadmaps calculation:', {
      roadmapsLength: all.length,
      selectedCourseIdValue,
      hasCourseIdInRoadmaps,
      roadmaps: all
    });
    if (!selectedCourseIdValue || !hasCourseIdInRoadmaps) {
      console.log('Returning all roadmaps');
      return all;
    }
    const filtered = all.filter(r => (r.CourseId || r.courseId) === selectedCourseIdValue);
    console.log('Filtered roadmaps:', filtered.length);
    return filtered;
  }, [roadmaps, selectedCourseIdValue, hasCourseIdInRoadmaps]);

  // Helper function to check if user has completed any lessons (topics)
  const hasCompletedAnyLesson = useMemo(() => {
    if (!roadmaps || roadmaps.length === 0) return false;
    
    for (const roadmap of roadmaps) {
      const roadmapLessons = lessons[roadmap.Id] || [];
      if (roadmapLessons.some(lesson => lesson.IsCompleted)) {
        return true;
      }
    }
    return false;
  }, [roadmaps, lessons]);

  // Subscription status state
  const [subscriptionStatus, setSubscriptionStatus] = useState({ 
    tier: 'free', 
    isActive: false, 
    endDate: null 
  });

  // Helper function to check if user is on free plan
  const isUserOnFreePlan = useMemo(() => {
    return subscriptionStatus.tier === 'free' || 
           subscriptionStatus.tier === 'basic' ||
           !subscriptionStatus.isActive;
  }, [subscriptionStatus]);

  // Helper function to get completion date (use the latest roadmap completion date)
  const getCompletionDate = useMemo(() => {
    if (!isGoalCompleted) return null;
    
    // If goal has a completion date, use that
    if (activeGoal?.completedAt || activeGoal?.CompletedAt) {
      return new Date(activeGoal.completedAt || activeGoal.CompletedAt);
    }
    
    // Otherwise, find the latest roadmap completion date
    let latestDate = null;
    roadmaps.forEach(roadmap => {
      if (roadmap.IsCompleted && roadmap.UpdatedAt) {
        const roadmapDate = new Date(roadmap.UpdatedAt);
        if (!latestDate || roadmapDate > latestDate) {
          latestDate = roadmapDate;
        }
      }
    });
    
    return latestDate;
  }, [isGoalCompleted, activeGoal, roadmaps]);

  // Load user's courses and determine active course
  const loadUserCoursesAndLearningData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Loading user courses and learning data...');

      const data = await learningPlanService.getOptimizedLearningPlanData();
      console.log('Received optimized learning plan data:', data);

      // Adapt the data structure to match expected format
      const adaptedData = {
        ...data,
        ActiveCourseId: data.ActiveCourseId, // Use the correct active course ID from backend
        Courses: data.Courses ? data.Courses.map(course => ({
          Course: {
            Id: course.id || course.Id,
            Title: course.title || course.Title,
            IsActive: course.isActive || course.IsActive,
            ImageUrl: course.imageUrl || course.ImageUrl
          },
          CompletionPercentage: course.completionPercentage || course.CompletionPercentage || 0
        })) : []
      };

      console.log('Adapted courses data:', adaptedData);
      setCoursesData(adaptedData);

            // Determine which course to select
      let courseToSelect = '';

      if (adaptedData.Courses.length > 0) {
        courseToSelect = adaptedData.Courses[0].Course.Id.toString();
        console.log('Active course from backend:', courseToSelect);
      }

      // If we have a course to select, set it
      if (courseToSelect) {
        console.log('Setting selected course ID and loading data...');
        setSelectedCourseId(courseToSelect);
        // Use the roadmaps data already received from the first API call
        setRoadmaps(data.Roadmaps || []);
        setLessons(data.LessonsForRoadmaps || {});
        setFirstUncompletedLessonRoadmapId(data.FirstUncompletedLessonRoadmapId);

        if (data.FirstUncompletedLesson) {
          setFirstUncompletedLessonId(data.FirstUncompletedLesson.Id);
        }

        // Set expanded state for roadmaps
        const initialExpandedState = {};
        if (data.FirstUncompletedLessonRoadmapId) {
          initialExpandedState[data.FirstUncompletedLessonRoadmapId] = true;
        }
        setExpandedRoadmaps(initialExpandedState);

        setLoading(false);
        // Return selected course id so caller can trigger handleCourseChange
        return courseToSelect;
      } else {
        console.log('No course to select, setting loading to false');
        setLoading(false);
        return '';
      }

    } catch (error) {
      console.error('Error loading user courses:', error);
      setError('Failed to load courses. Please try again.');
      setLoading(false);
      return '';
    }
  }, [courseId]);

  // Load learning data for a specific course using optimized endpoint
  const loadCourseLearningDataOptimized = useCallback(async (courseId) => {
    if (!courseId) return;

    try {
      setLoadingCourse(true);
      console.log('Loading optimized learning data for course:', courseId);

      const data = await learningPlanService.getOptimizedLearningPlanData(courseId);
      console.log('Received optimized course learning data:', data);

      // Update the learning plan data with the optimized response structure
      setRoadmaps(data.Roadmaps || []);
      setLessons(data.LessonsForRoadmaps || {});
      setFirstUncompletedLessonRoadmapId(data.FirstUncompletedLessonRoadmapId);

      if (data.FirstUncompletedLesson) {
        setFirstUncompletedLessonId(data.FirstUncompletedLesson.Id);
      }

      // Set expanded state for roadmaps
      const initialExpandedState = {};
      if (data.FirstUncompletedLessonRoadmapId) {
        initialExpandedState[data.FirstUncompletedLessonRoadmapId] = true;
      }
      setExpandedRoadmaps(initialExpandedState);

    } catch (error) {
      console.error('Error loading optimized course learning data:', error);
      setError('Failed to load learning plan data. Please try again.');
    } finally {
      setLoadingCourse(false);
    }
  }, []);

  // Handle course selection change
  const handleCourseChange = useCallback(async (newCourseId) => {
    setSelectedCourseId(newCourseId);
    // Load data for the new course using optimized endpoint
    await loadCourseLearningDataOptimized(newCourseId);
  }, [navigate, loadCourseLearningDataOptimized]);

  // Completely rewrite the checkAndGenerateNextLessons function
  const checkAndGenerateNextLessons = useCallback(async () => {
    // If already checking, don't start another check
    if (isCheckingForLessonsRef.current) {
      console.log("Already checking for lessons to generate, skipping duplicate call");
      return;
    }
    
    // Throttle checks - min 10 seconds between checks
    const now = Date.now();
    if (now - lastApiCallTimeRef.current < 10000) {
      console.log("Lesson generation check throttled - too soon since last API call");
      return;
    }
    
    try {
      isCheckingForLessonsRef.current = true;
      console.log("Checking if all lessons are completed for the current roadmap...");
      
      // Use refs instead of state to avoid dependency changes triggering effects
      const roadmapsToCheck = lastRoadmapsRef.current;
      const lessonsToCheck = lastLessonsRef.current;
      const firstUncompletedLessonIdToCheck = firstUncompletedLessonIdRef.current;
      
      // If there's no first uncompleted lesson ID, all lessons might be completed
      if (!firstUncompletedLessonIdToCheck) {
        // Get all roadmaps sorted by their index/position
        const sortedRoadmaps = [...roadmapsToCheck].sort((a, b) => {
          // First try to sort by ID if those are sequential
          return a.Id - b.Id;
        });
        
        // Find the last roadmap that has all lessons completed
        let lastCompletedRoadmapIndex = -1;
        
        for (let i = 0; i < sortedRoadmaps.length; i++) {
          const item = sortedRoadmaps[i];
          const itemLessons = lessonsToCheck[item.Id] || [];
          
          // Skip items with no lessons
          if (itemLessons.length === 0) continue;
          
          const allCompleted = itemLessons.every(lesson => lesson.IsCompleted);
          
          if (allCompleted) {
            lastCompletedRoadmapIndex = i;
          } else {
            break; // Stop at first item with incomplete lessons
          }
        }
        
        // If we found a completed roadmap and there's a next one
        if (
          lastCompletedRoadmapIndex >= 0 && 
          lastCompletedRoadmapIndex < sortedRoadmaps.length - 1
        ) {
          const nextRoadmap = sortedRoadmaps[lastCompletedRoadmapIndex + 1];
          const nextRoadmapLessons = lessonsToCheck[nextRoadmap.Id] || [];
          
          // Only generate lessons if there are none for this roadmap
          if (nextRoadmapLessons.length === 0) {
            console.log(`Generating lessons for next roadmap ID: ${nextRoadmap.Id}`);
            
            // Set a flag in localStorage to avoid duplicate calls across page reloads
            const lastGenKey = `lastLessonGen_${nextRoadmap.Id}`;
            const lastGenTime = localStorage.getItem(lastGenKey);
            const now = Date.now();
            
            // Only allow generation once every 5 minutes for the same roadmap
            if (!lastGenTime || (now - parseInt(lastGenTime)) > 300000) {
              localStorage.setItem(lastGenKey, now.toString());
              
              // Generate lessons with API call
              await lessonService.generateLessonsForRoadmap(nextRoadmap.Id);
              
                await loadUserCoursesAndLearningData();
            } else {
              console.log(`Skipping lesson generation for roadmap ${nextRoadmap.Id}: recently generated`);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error checking for completed lessons or generating new ones:", error);
    } finally {
      // Always reset the flag
      isCheckingForLessonsRef.current = false;
    }
  }, []);

   // Initial load: fetch courses, then select and load the first (or URL-specified) course
   useEffect(() => {
     (async () => {
       const selected = await loadUserCoursesAndLearningData();
       if (selected) {
         await handleCourseChange(selected);
       }
     })();
   }, [loadUserCoursesAndLearningData, handleCourseChange]);

   // Handle course change from URL
   useEffect(() => {
     if (courseId && courseId !== selectedCourseId && coursesData) {
       handleCourseChange(courseId);
     }
   }, [courseId, selectedCourseId, coursesData, handleCourseChange]);

  // Fetch subscription status on mount
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        const subStatus = await subscriptionService.getStatus();
        setSubscriptionStatus(subStatus);
      } catch (error) {
        console.error('Error fetching subscription status:', error);
        // Default to free subscription if error
        setSubscriptionStatus({ tier: 'free', isActive: false, endDate: null });
      }
    };
    
    fetchSubscriptionStatus();
  }, []);

  // Add layout restoration listener
  useEffect(() => {
    const handleLayoutRestoration = () => {
      // Force layout recalculation when layout is restored
      const mainContent = document.querySelector('.app-layout .learning-plan-main-content');
      if (mainContent) {
        // Ensure proper layout styles are applied
        mainContent.style.marginLeft = '';
        mainContent.style.width = '';
        
        // Force reflow
        // eslint-disable-next-line no-unused-expressions
        mainContent.offsetHeight;
        
        console.log('Layout restored in LearningPlan component');
      }
    };
    
    // Listen for layout restoration events
    window.addEventListener('layout-restored', handleLayoutRestoration);
    
    return () => {
      window.removeEventListener('layout-restored', handleLayoutRestoration);
    };
  }, []);

  // Completely rewrite the visibility/navigation effects
  useEffect(() => {
    // Handler for when the page becomes visible
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log('Page became visible, refreshing learning plan data');
        await loadUserCoursesAndLearningData();
        
        // Use a setTimeout to ensure it's not in the same execution cycle
        setTimeout(() => {
          if (!isCheckingForLessonsRef.current) {
            checkAndGenerateNextLessons();
          }
        }, 1000);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Initial check after component mount with delay
    const initialCheckTimeoutId = setTimeout(() => {
      if (!isCheckingForLessonsRef.current) {
        checkAndGenerateNextLessons();
      }
    }, 3000); // Longer delay to ensure all state is properly initialized
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimeout(initialCheckTimeoutId);
    };
  // IMPORTANT: Don't include checkAndGenerateNextLessons in the dependency array
  }, [loadUserCoursesAndLearningData]);


  // Lesson limit modal functions
  const closeLessonLimitModal = () => {
    setShowLessonLimitModal(false);
  };

  const handleGetPro = () => {
    closeLessonLimitModal();
    navigate('/settings', { state: { activeTab: 'subscription' } });
  };
  
  // Toggle roadmap expanded/collapsed state  
  const toggleRoadmapExpanded = useCallback((itemId) => {    
  // Check if this roadmap exists    
  const roadmap = roadmaps.find(item => item.Id === itemId);    
  if (!roadmap) return;        
  // For debugging - log the roadmap content    
  // console.log('Toggle roadmap:', itemId, roadmap);       
  // Allow expanding/collapsing all roadmaps regardless of completion status    
  setExpandedRoadmaps(prev => ({      ...prev,      [itemId]: !prev[itemId]    }));  }, [roadmaps]);


  // Function to handle starting a lesson - optimized to use refs instead of state
  const handleStartLesson = useCallback(async (lesson, event, onComplete) => {
    // Prevent default link behavior
    event.preventDefault();
    
    // Pro users should never see the limit modal
    if (subscriptionStatus.tier === 'pro' && subscriptionStatus.isActive) {
      navigate(`/lessons/${lesson.Id}`);
      return;
    }
    
    // Check if user is on free plan and has already completed a lesson
    if (isUserOnFreePlan && hasCompletedAnyLesson && !lesson.IsCompleted) {
      setShowLessonLimitModal(true);
      return;
    }
    
    navigate(`/lessons/${lesson.Id}`);
  }, [navigate, isUserOnFreePlan, hasCompletedAnyLesson, subscriptionStatus]);

  // Function to force a refresh of learning plan data
  const handleRefreshData = useCallback(() => {
    loadUserCoursesAndLearningData();
  }, [loadUserCoursesAndLearningData]);

  // Function to set a course as active
  const handleSetActiveCourse = useCallback(async (courseId) => {
    if (!courseId) return;

    try {
      console.log('Setting course as active:', courseId);
      await courseService.setActiveCourse(courseId);

      // Refresh the courses data to reflect the change
      await loadUserCoursesAndLearningData();
      console.log('Course set as active successfully');
    } catch (error) {
      console.error('Error setting course as active:', error);
      setError('Failed to set course as active. Please try again.');
    }
  }, [loadUserCoursesAndLearningData]);

  // Add function to handle creating a learning plan
  const handleCreateLearningPlan = async () => {
    try {
      setCreating(true);

      // Extract selected topics from course selection if available
      let selectedTopics = null;

      const targetCourseId = selectedCourseId ? parseInt(selectedCourseId) : coursesData.Courses[0].Course.Id;

      // Call the API to create roadmaps with courseId in the path
      const response = await roadmapService.createRoadmaps(selectedTopics, targetCourseId);

      if (response) {
        console.log("Roadmaps created successfully:", response);

        // Roadmaps are now automatically associated with courses in the backend
        console.log(`Roadmaps created and associated with course ${targetCourseId}`);

        // Refresh the data
        await loadCourseLearningDataOptimized(targetCourseId);
      }
    } catch (error) {
      console.error('Error creating learning plan:', error);

      // Handle rate limiting error specifically
      if (error.response?.status === 402) {
      } else {
        setError('Failed to create learning plan. Please try again later.');
      }
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="app-layout">
        <SideMenu />
        <div className={`learning-plan-main-content ${theme}`}>
          <div className="sb-loading-container">
            <div className="loading-icon-container">
              <div className="empty-state-icon">📚</div>
            </div>
            <div className="sb-loading-row">
              <div className="sb-loading-spinner"></div>
              <p className="sb-loading-text">Loading Roadmap...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    // Check if the error is about no active goal
    const isNoActiveGoalError = error.includes('No active goal found');
    
    return (
      <div className="app-layout">
        <SideMenu />
        <div className={`learning-plan-main-content ${theme}`}>
          <div className="learning-plan-container error-container">
            {isNoActiveGoalError ? (
              <div className="no-active-goal">
                <div className="empty-state-icon">🎯</div>
                <p>You don't have an active goal set.</p>
                <p>Go to the goals page and pick the next goal you will achieve.</p>
                <button 
                  className="go-to-goals-button" 
                  onClick={() => navigate('/goals')}
                >
                  <span>
                    <Target size={16} />
                    Go to Goals
                  </span>
                </button>
              </div>
            ) : (
              <>
                <p className="error-message">{error}</p>
                
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button 
                    className="learning-plan-button" 
                    onClick={() => window.location.reload()}
                  >
                    <span>
                      <svg className="lp-refresh-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.65 6.35A8 8 0 1 0 19.73 14h-2.08A6 6 0 1 1 16 7.89V10l4-4-4-4v2.73z" fill="currentColor" />
                      </svg>
                      Reload Page
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // If there are no roadmaps, show an empty state
  if (!roadmaps || roadmaps.length === 0) {
    return (
      <div className="app-layout">
        <SideMenu />
        <div className={`learning-plan-main-content ${theme}`}>
          <div className={`learning-plan-container ${theme}`}>
            <div className="learning-plan-header">
              <div className="panel-title">
                <Map size={24} />
                <h2 className="learning-plan-header-title">Courses</h2>
              </div>
              {/* Course selector */}
            {coursesData && coursesData.Courses && coursesData.Courses.length > 0 && (
                <div className="lp-course-selector">
                  <label className="lp-course-selector-label">Course:</label>
                  <select
                    className="lp-course-selector-select"
                    value={selectedCourseId}
                    onChange={(e) => handleCourseChange(e.target.value)}
                    disabled={loadingCourse}
                  >
                    {coursesData.Courses.map(courseData =>
                      courseData?.Course?.Id ? (
                        <option key={courseData.Course.Id} value={courseData.Course.Id.toString()}>
                          {courseData.Course.Title}
                        </option>
                      ) : null
                    )}
                  </select>
                </div>
              )}
            </div>
            {/* Loading course data */}
            {loadingCourse && (
              <div className="sb-loading-container">
                <div className="sb-loading-spinner"></div>
                <p className="sb-loading-text">Loading course data...</p>
              </div>
            )}

            {/* No courses available state */}
            {!loadingCourse && coursesData && (!coursesData.Courses || coursesData.Courses.length === 0) && (
              <div className="lp-course-empty">
                <div className="empty-state-icon">📚</div>
                <p>You don't have any courses yet</p>
                <p>Start by setting up your learning goals to get personalized courses.</p>
                <button
                  className="go-to-goals-button"
                  onClick={() => navigate('/goals')}
                >
                  <span>
                    <Target size={16} />
                    Go to Goals
                  </span>
                </button>
              </div>
            )}

            {/* No course selected state */}
            {!loadingCourse && !selectedCourseId && coursesData && coursesData.Courses && coursesData.Courses.length > 0 && (
              <div className="lp-course-empty">
                <div className="empty-state-icon">🎓</div>
                <p>Select a course to view your learning plan</p>
              </div>
            )}

            {/* Course has no roadmaps - show start button */}
            {!loadingCourse && selectedCourseId && roadmaps && roadmaps.length === 0 && (
              <div className="lp-course-empty">
                <div className="empty-state-icon">🎓</div>
                <p>Let's start with this course</p>
                <button
                  onClick={handleCreateLearningPlan}
                  disabled={creating}
                  className="learning-plan-button"
                >
                  <span>
                    <svg className="sb-loading-spinner" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    </svg>
                    Start
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <SideMenu />
      <div className={`learning-plan-main-content ${theme}`}>
        <div className={`learning-plan-container ${theme}`}>
          <div className="learning-plan-header">
            <div className="panel-title">
              <Map size={24} />
              <h2 className="learning-plan-header-title">Courses</h2>
            </div>
            {/* Course selector */}
            {coursesData && coursesData.Courses && coursesData.Courses.length > 0 && (
              <div className="lp-course-selector">
                <label className="lp-course-selector-label">Course:</label>
                <select
                  className="lp-course-selector-select"
                  value={selectedCourseId}
                  onChange={(e) => handleCourseChange(e.target.value)}
                  disabled={loadingCourse}
                >
                  {coursesData.Courses.map(courseData =>
                    courseData?.Course?.Id ? (
                      <option key={courseData.Course.Id} value={courseData.Course.Id.toString()}>
                        {courseData.Course.Title}
                      </option>
                    ) : null
                  )}
                </select>
              </div>
            )}
          </div>
          
          {/* Goal Card */}
          <div className={`learning-plan-goal-card ${theme} ${isGoalCompleted ? 'completed' : ''}`}>
            <div className="learning-plan-goal-content-wrapper">
              <div className="goal-thumbnail">
                {/* Goal Image */}
                {(() => {
                  if (coursesData && coursesData.Courses && selectedCourseId) {
                    const selectedCourse = coursesData.Courses.find(courseData =>
                      courseData?.Course?.Id === parseInt(selectedCourseId)
                    );

                    if (selectedCourse?.Course?.ImageUrl !== null) {
                      return (
                        <img 
                        src={selectedCourse.Course.ImageUrl.startsWith('http') 
                          ? selectedCourse.Course.ImageUrl 
                          : `https://nd69zr.realhost-free.net${selectedCourse.Course.ImageUrl}`
                        } 
                        alt={selectedCourse.Course.Title || 'Course Image'} 
                        className="goal-image"
                        onError={(e) => {
                          console.error("Goal image failed to load:", selectedCourse.Course.ImageUrl);
                          e.target.onerror = null;
                          e.target.src = `https://nd69zr.realhost-free.net/goal-images/default-goal.svg`;
                        }}
                      />
                      );
                    } else {
                      return (
                        <div className="goal-icon">
                        <i className="goal-icon-image"></i>
                      </div>
                      );
                    }
                  }
                  return (
                    <div className="goal-icon">
                      <i className="goal-icon-image"></i>
                    </div>
                  );
                })()}
                
                {/* Completion Badge Overlay */}
                {isGoalCompleted && (
                  <div className="goal-completion-badge">
                    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="12" fill="#10B981"/>
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white" />
                    </svg>
                  </div>
                )}
              </div>
              
              <div className="learning-plan-goal-text">
                <div className="learning-plan-goal-title-container">
                  <div className="learning-plan-goal-title">
                    {(() => {
                      // Find the selected course
                      if (coursesData && coursesData.Courses && selectedCourseId) {
                        const selectedCourse = coursesData.Courses.find(courseData =>
                          courseData?.Course?.Id === parseInt(selectedCourseId)
                        );
                        return selectedCourse?.Course?.Title || 'Course';
                      }
                      return 'Course';
                    })()}
                  </div>

                  {/* Active Course Badge or Set Active Button */}
                  {(() => {
                    if (coursesData && coursesData.Courses && selectedCourseId) {
                      const selectedCourse = coursesData.Courses.find(courseData =>
                        courseData?.Course?.Id === parseInt(selectedCourseId)
                      );

                      if (selectedCourse?.Course?.IsActive) {
                        return (
                          <div className="course-active-badge">
                            <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="12" cy="12" r="10" fill="#c8746d"/>
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white"/>
                            </svg>
                            <span>Active Course</span>
                          </div>
                        );
                      } else if (selectedCourse) {
                        return (
                          <button
                            className="set-active-course-button"
                            onClick={() => handleSetActiveCourse(selectedCourseId)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor"/>
                            </svg>
                            <span>Set as Active</span>
                          </button>
                        );
                      }
                    }
                    return null;
                  })()}
                </div>
                
                {/* Progress and Completion Info */}
                <div className="goal-progress-info">
                  {isGoalCompleted ? (
                    <div className="goal-completion-info">
                      <div className="goal-completion-status">
                        <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor" />
                        </svg>
                        <span>Course Completed!</span>
                        <div className="goal-final-progress">
                          Completed on {getCompletionDate ? getCompletionDate.toLocaleDateString() : new Date().toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="goal-progress-container">
                      <div className="goal-progress-bar">
                        <div 
                          className="goal-progress-fill" 
                          style={{ width: `${getOverallCompletionPercentage}%` }}
                        ></div>
                      </div>
                      <div className="goal-progress-info">
                        <span className="goal-progress-percentage">{getOverallCompletionPercentage}% complete</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <RenderLearningPlan
            roadmaps={visibleRoadmaps}
            lessons={lessons}
            expandedRoadmaps={expandedRoadmaps}
            toggleRoadmapExpanded={toggleRoadmapExpanded}
            firstUncompletedLessonRoadmapId={firstUncompletedLessonRoadmapId}
            firstUncompletedLessonId={firstUncompletedLessonId}
            generatingContentRef={generatingContentRef}
            handleStartLesson={handleStartLesson}
            handleRefreshData={handleRefreshData}
            creating={creating}
            theme={theme}
          />
        </div>
      </div>

      {/* Lesson Limit Modal */}
      <div className={`roadmap-modal ${showLessonLimitModal ? 'show' : ''}`}>
        <div className={`modal-content ${theme}`}>
          <div className="modal-header">
            <h3 className="modal-title">Free Plan Limit Reached</h3>
            <button 
              className="close-button" 
              onClick={closeLessonLimitModal}
            >
              ×
            </button>
          </div>
          <div className="modal-body">
            <div className="lesson-limit-warning">
              <div className="limit-icon">⚠️</div>
              <div className="limit-message">
                <p>You've completed 1 topic in this course. Free plan users can only complete 1 topic per course.</p>
              </div>
              <div className="limit-suggestion">
                <p>To continue learning and unlock unlimited topics, upgrade to Pro and get:</p>
              </div>
              <div className="limit-features">
                <h4>Pro Features:</h4>
                <ul>
                  <li>✨ Up to 10 goals in progress</li>
                  <li>💬 Unlimited AI Chat Queries</li>
                  <li>📚 Unlimited AI Lessons</li>
                  <li>📝 Unlimited AI Assessments</li>
                  <li>🤖 Best AI Models</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button 
              className="cancel-button" 
              onClick={closeLessonLimitModal}
            >
              Maybe Later
            </button>
            <button 
              className="confirm-button upgrade" 
              onClick={handleGetPro}
            >
              Upgrade to Pro
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningPlan; 